import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, ClipboardLabel} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {AfriexOTCTrade} from '@/types';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {formatName} from '@/utils/formatName';

import OTCActionButton from './OTCActionButton';

type OTCTradeSummaryProp = {
  item: AfriexOTCTrade;
  handleApprove: (id: string) => void;
  extraRow?: React.ReactNode;
};

const OTCTradeSummary = ({
  item,
  handleApprove,
  extraRow,
}: OTCTradeSummaryProp) => {
  const tradeId = item?.id;
  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">OTC Trade Details</h2>
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Reference ID
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            <ClipboardLabel>{tradeId || ''}</ClipboardLabel>
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date of Input
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(item?.createdAt || '')}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Transaction DateTime
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(item?.timestamp?.toString() || '', true)}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Amount Sent
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item.fromAmount?.toLocaleString()} {item.fromSymbol}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Amount Received
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item.toAmount?.toLocaleString()} {item.toSymbol}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Balance Remaining
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item.availableAmount?.toLocaleString()} {item.toSymbol}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">Rate</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatValues(Number(item.rate), 'rate', 6)}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Submitted By
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.submittedBy ? (
              formatName(item.submittedBy)
            ) : (
              <span className="text-red-400">N/A</span>
            )}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Approved By
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.approvedBy ? (
              formatName(item.approvedBy)
            ) : (
              <span className="text-red-400">Not Approved</span>
            )}
          </p>
        </div>
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          {/* `id` is optional, and without one there is nothing to approve:
              calling `handleApprove(undefined)` would PATCH
              `/v2/admin/otc-volume/undefined`. */}
          {!item?.approvedBy && tradeId && (
            <PermissionsProvider
              permission={AfriexAdminPermission.OTCVolumeManage}
            >
              <Button
                variant="solid"
                colorScheme="green"
                onClick={() => {
                  handleApprove(tradeId);
                  return false;
                }}
              >
                Approve
              </Button>
            </PermissionsProvider>
          )}

          <PermissionsProvider
            permission={AfriexAdminPermission.OTCVolumeManage}
          >
            {!item?.approvedBy?.id ? (
              <OTCActionButton initialData={item} />
            ) : null}
          </PermissionsProvider>
        </div>
      </div>

      {extraRow || ''}
    </div>
  );
};

export default OTCTradeSummary;
